import Head from "next/head";
import Link from "next/link";
import { useRouter, withRouter } from "next/router";
import { AdminLayout } from "components/admin/admin-layout";
import {
  useGetPostsForAdminPanelLazyQuery,
  useGetPostsForAdminPanelQuery,
} from "lib/graphql-generated";
import { useEffect, useState } from "react";
import { useInputArray } from "lib/hooks/use-input-group";
import { gql } from "@apollo/client";
import { postTypes } from "lib/types";
import { Button } from "components/buttons/button";
import { AdminSearchBar } from "components/admin/admin-search-bar";
import { useInput } from "lib/hooks/use-input";

const GET_ALL_POSTS = gql`
  query GetPostsForAdminPanel(
    $limit: Int
    $offset: Int
    $type: [String!]
    $search: String
  ) {
    posts_aggregate(
      where: {
        _and: [{ type: { _in: $type } }, { title: { _ilike: $search } }]
      }
    ) {
      aggregate {
        count
      }
    }
    posts(
      order_by: [{ created_at: desc }]
      limit: $limit
      offset: $offset
      where: {
        _and: [{ type: { _in: $type } }, { title: { _ilike: $search } }]
      }
    ) {
      id
      title
      type
      slug
      author {
        name
      }
      created_at
    }
  }
`;

export const Posts = () => {
  const search = useInput<string>("");
  /**
   * Query Strings
   */
  const router = useRouter();
  const pQuery = parseInt(router.query["p"] as string);
  let paginate = Boolean(pQuery) ? pQuery : 0;
  const limit = 25;
  const offset = Boolean(paginate) ? paginate * limit : 0;

  const postTypeFilters = useInputArray(postTypes.map((type) => type.name));

  const { data, loading } = useGetPostsForAdminPanelQuery({
    variables: {
      limit: limit,
      offset: offset,
      type: postTypeFilters.values,
      search: search.value === "" ? "%%" : `%${search.value}%`,
    },
    fetchPolicy: "cache-and-network",
  });

  const handleSearch = (e) => {};

  useEffect(() => {
    router.push(`/admin/posts?p=0`);
  }, [search.value]);

  return (
    <>
      <AdminLayout
        page='Posts'
        searchBar={
          <AdminSearchBar
            placeholder='Search Posts by title'
            onSearch={handleSearch}
            {...search.bind}
          />
        }>
        <article className='space-y-4'>
          <header className='flex flex-col xl:flex-row flex-wrap w-full  space-y-4 xl:space-y-0 '>
            <h1 className='header text-4xl ml-auto mr-auto xl:ml-0 xl:mr-0 order-1'>
              Posts
            </h1>
            <form className='flex flex-row space-x-8 ml-auto mr-auto xl:mr-0  order-3 xl:order-2'>
              <h2 className='text-xl mt-1 font-semibold text-gray-800'>
                Types:
              </h2>
              {postTypes.map((type, index) => (
                <div key={index} className='my-auto'>
                  <label
                    htmlFor={type.name}
                    className='inline-flex items-center cursor-pointer'>
                    <input
                      id={type.name}
                      type='checkbox'
                      className={`form-checkbox text-${type.color}`}
                      value={type.name}
                      checked={postTypeFilters.values.includes(type.name)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          postTypeFilters.addValue(e.target.value);
                        } else {
                          postTypeFilters.removeValue(e.target.value);
                        }
                      }}
                    />
                    <span className='ml-2 capitalize'>{type.name + "s"}</span>
                  </label>
                </div>
              ))}
            </form>
            <Button
              buttonType='secondary'
              buttonSize='lg'
              href='/admin/posts/new'
              className='my-auto ml-auto mr-auto xl:mr-0 xl:ml-4 order-2 xl:order-3'>
              <svg
                className='w-4 h-4 mr-2 -ml-1 mb-1'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              <span>New Post</span>
            </Button>
          </header>

          <table className='min-w-full divide-y divide-gray-200 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg'>
            <thead className=' bg-gray-100 text-left text-xs leading-4 font-medium text-gray-700 uppercase tracking-wider'>
              <tr>
                <th className='px-6 py-3'>Title</th>
                <th className='px-6 py-3'>Author</th>
                <th className='px-6 py-3'>Type</th>
                <th className='px-6 py-3'>Time</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 text-sm text-gray-800'>
              {loading ? (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={4}>
                    Loading Data...
                  </td>
                </tr>
              ) : !data || data?.posts.length === 0 ? (
                <td
                  className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                  colSpan={4}>
                  Could not find posts...
                </td>
              ) : (
                <>
                  {data.posts.map((post) => (
                    <tr
                      key={post.id}
                      className='hover:bg-gray-300 cursor-pointer'
                      onClick={() => router.push(`/admin/posts/${post.slug}`)}>
                      <td className='px-6 py-4 whitespace-no-wrap leading-5 space-x-4'>
                        <span
                          className={`${
                            post.type === "review"
                              ? "bg-yellow-default"
                              : post.type === "list"
                              ? "bg-red-default"
                              : "bg-green-default"
                          } w-2 h-2 rounded-full inline-block`}></span>
                        <span>{post.title}</span>
                      </td>
                      <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                        {post.author.name}
                      </td>
                      <td className='px-6 py-4 whitespace-no-wrap leading-5 capitalize'>
                        {post.type}
                      </td>
                      <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                        {new Date(post.created_at).toLocaleDateString("en-GB")}
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </article>
        {data && (
          <nav
            className='py-3 flex items-center justify-between border-t border-gray-200 '
            aria-label='Pagination'>
            <div className='hidden sm:block'>
              <p className='text-sm text-gray-700'>
                Showing{" "}
                <span className='font-medium'>
                  {offset}
                  {` `}
                </span>
                to{" "}
                <span className='font-medium'>
                  {offset + limit}
                  {` `}
                </span>
                of{" "}
                <span className='font-medium'>
                  {data?.posts_aggregate.aggregate.count}
                  {` `}
                </span>
                results
              </p>
            </div>
            <div className='flex-1 flex justify-between sm:justify-end'>
              {paginate !== 0 && (
                <Link href={`/admin/posts?p=${paginate - 1}`} shallow>
                  <a className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Previous
                  </a>
                </Link>
              )}
              {offset + limit < data.posts_aggregate.aggregate.count && (
                <Link href={`/admin/posts?p=${paginate + 1}`} shallow>
                  <a className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Next
                  </a>
                </Link>
              )}
            </div>
          </nav>
        )}
      </AdminLayout>
    </>
  );
};

export default Posts;
